import React from 'react';
import { t, DEFAULT_LANG } from '@/lib/i18n';

/**
 * The two legal lines, and the distinction between them.
 *
 * `AgreeNotice` is a sign-in-wrap: the couple agrees to the Terms and the
 * Privacy Policy by pressing the button it sits under. That is only worth
 * anything if the links are conspicuous and adjacent to the action, so this
 * renders directly beneath the button, underlined, never in a collapsed
 * section — a link nobody can see is the whole reason browsewrap agreements
 * get thrown out.
 *
 * `GuestDataNotice` deliberately does NOT say "you agree to our Terms of
 * Service". A guest is not the customer: the Terms are a contract between us
 * and the couple, and asserting a guest agreed to them claims a contract that
 * does not exist. What a guest is owed is notification of purpose before their
 * data is collected — what is being collected, who sees it, how long it is
 * kept — which is what this states. Providing the answers is then the consent.
 *
 * Both link to the real pages rather than reproducing text, so there is one
 * copy to keep accurate.
 */

const linkClass =
  'underline underline-offset-2 hover:text-gold-dark transition-colors duration-200';

/** Sign-in-wrap for couples. Place immediately below the button it refers to. */
export function AgreeNotice({ action = 'continuing', className = '' }) {
  return (
    <p className={`font-body text-xs text-charcoal/50 text-center leading-relaxed ${className}`}>
      By {action}, you agree to our{' '}
      <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className={linkClass}>
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href="/terms.html" target="_blank" rel="noopener noreferrer" className={linkClass}>
        Terms of Service
      </a>
      .
    </p>
  );
}

/**
 * Notice for guests, shown before they answer anything.
 *
 * Names the three things a guest actually wants to know and would not
 * otherwise guess: the couple sees this, allergy notes reach the caterer, and
 * it does not live forever.
 */
export function GuestDataNotice({ lang = DEFAULT_LANG, className = '' }) {
  return (
    // The link sits on its own line rather than trailing the sentence: appended
    // inline it reads as a dangling fragment ("…after the wedding. Privacy
    // Policy") in all three languages, and no phrasing that joins them works
    // across en/zh/th without a separate connector string per language.
    <div className={`font-body text-xs text-charcoal/50 leading-relaxed space-y-1 ${className}`}>
      <p>{t('rsvp.privacy_notice', lang)}</p>
      <p>
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className={linkClass}>
          {t('rsvp.privacy_link', lang)}
        </a>
      </p>
    </div>
  );
}
